import React, { useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  RedirectToSignIn,
  SignInButton,
  UserButton,
  useUser,
  useClerk
} from "@clerk/clerk-react";
import {
  Upload,
  FileText,
  Zap,
  Brain,
  Shield,
  CheckCircle2,
  ArrowRight,
  Play,
  Github,
  Twitter,
  Linkedin,
  X,
  MessageSquare,
  Layers,
  Check,
  ChevronDown
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { motion } from 'framer-motion';
import { BentoGrid, BentoCard } from "@/components/ui/bento-grid";
import { AnimatedBeam } from "@/components/ui/animated-beam";

import Dashboard from './Dashboard';
import ChatInterface from './ChatInterface';
import StudyTools from './StudyTools';
import { DocumentsProvider } from './contexts/DocumentsContext';

// Access the publishable key from environment variables
const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!clerkPubKey) {
  console.error("Missing Publishable Key");
}

const features = [
  {
    name: "Save your files",
    description: "We automatically save your files as you type.",
    cta: "Learn more",
    className: "lg:col-span-1 lg:row-span-2",
    Icon: FileText,
    background: (
      <div className="absolute inset-0 flex flex-col gap-2 p-4 pt-16 opacity-60 mask-gradient-to-b from-black to-transparent">
        <div className="flex items-center gap-3 bg-white p-3 rounded-lg shadow-sm border border-slate-100 transform -rotate-2 hover:rotate-0 transition-transform">
          <FileText className="w-8 h-8 text-blue-500" />
          <div className="flex flex-col">
            <span className="text-sm font-bold text-slate-800">Biology_Notes.pdf</span>
            <span className="text-[10px] text-slate-500">2.4 MB • Just now</span>
          </div>
        </div>
        <div className="flex items-center gap-3 bg-white p-3 rounded-lg shadow-sm border border-slate-100 transform rotate-1 hover:rotate-0 transition-transform">
          <FileText className="w-8 h-8 text-purple-500" />
          <div className="flex flex-col">
            <span className="text-sm font-bold text-slate-800">History_Essay.docx</span>
            <span className="text-[10px] text-slate-500">1.1 MB • 2 hrs ago</span>
          </div>
        </div>
        <div className="flex items-center gap-3 bg-white p-3 rounded-lg shadow-sm border border-slate-100 transform -rotate-1 hover:rotate-0 transition-transform">
          <Upload className="w-8 h-8 text-green-500" />
          <div className="flex flex-col">
            <span className="text-sm font-bold text-slate-800">Lecture_Audio.mp3</span>
            <span className="text-[10px] text-slate-500">14 MB • 5 hrs ago</span>
          </div>
        </div>
      </div>
    ),
  },
  {
    name: "Chat with your Documents",
    description: "Ask questions, get clarifications, and explore your study materials.",
    cta: "Learn more",
    className: "lg:col-span-2 lg:row-span-1",
    Icon: MessageSquare,
    background: (
      <div className="absolute inset-0 top-8 left-8 p-4 opacity-100">
        <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-4 w-full max-w-md space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold text-xs">AI</div>
            <div className="bg-slate-50 p-3 rounded-lg rounded-tl-none text-xs text-slate-600 flex-1">
              Generating summary for "Chapter 4: Cell Structure"...
            </div>
          </div>
          <div className="flex items-center gap-2 pl-11">
            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full w-2/3 bg-purple-500 rounded-full animate-pulse"></div>
            </div>
            <span className="text-[10px] text-slate-400">Processing</span>
          </div>
        </div>

        <div className="absolute right-0 top-1/2 translate-x-2 -translate-y-1/2 bg-white rounded-xl shadow-lg border border-slate-100 p-3 flex items-center gap-3 opacity-60 scale-90">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600"><MessageSquare size={12} /></div>
          <div className="text-xs font-medium text-slate-700">New message from AI</div>
        </div>
      </div>
    ),
  },
  {
    name: "Deep Understanding",
    description: "Visual concept maps and relationship extraction.",
    cta: "Learn more",
    className: "lg:col-span-2 lg:row-span-1",
    Icon: Zap,
    background: (
      <div className="absolute inset-0 flex items-center justify-center opacity-30">
        <div className="relative w-full h-full p-8 flex items-center justify-center">
          <div className="absolute w-24 h-24 rounded-full border-2 border-purple-200 flex items-center justify-center z-10 bg-white">
            <Brain size={32} className="text-purple-600" />
          </div>

          {/* Orbital Elements */}
          <div className="absolute w-48 h-48 rounded-full border border-dashed border-slate-300 animate-spin-slow"></div>
          <div className="absolute w-72 h-72 rounded-full border border-slate-100"></div>

          {/* Nodes */}
          <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-12 h-12 bg-white shadow-md rounded-lg flex items-center justify-center border border-slate-100 animate-float">
            <FileText size={20} className="text-blue-500" />
          </div>
          <div className="absolute top-1/4 right-1/3 w-10 h-10 bg-white shadow-md rounded-lg flex items-center justify-center border border-slate-100 animate-float-delayed">
            <Layers size={18} className="text-green-500" />
          </div>
          <div className="absolute bottom-1/3 right-1/4 w-14 h-14 bg-white shadow-md rounded-lg flex items-center justify-center border border-slate-100 animate-float-slow">
            <CheckCircle2 size={24} className="text-orange-500" />
          </div>
        </div>
      </div>
    ),
  },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useUser();
  const clerk = useClerk();

  const containerRef = useRef(null);
  const step1Ref = useRef(null);
  const step2Ref = useRef(null);
  const step3Ref = useRef(null);

  const [showTagline, setShowTagline] = React.useState(true);

  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowTagline(false);
      } else {
        setShowTagline(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const navbarHeight = 80; // Height of fixed navbar
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-mesh font-['Public_Sans',sans-serif] overflow-x-hidden selection:bg-purple-500/30">

      {/* Navbar */}
      <nav className="fixed w-full z-50 transition-all duration-300 bg-white/50 backdrop-blur-md border-b border-white/20">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-1">
            <img src="/logo.png" alt="Axora Logo" className="w-16 h-16 object-contain" />
          </div>

          <div className="hidden md:flex items-center gap-8">
            <a href="#features" onClick={(e) => scrollToSection(e, 'features')} className="text-sm font-medium text-slate-600 hover:text-purple-600 transition-colors">Features</a>
            <a href="#how-it-works" onClick={(e) => scrollToSection(e, 'how-it-works')} className="text-sm font-medium text-slate-600 hover:text-purple-600 transition-colors">How it Works</a>
            <a href="#pricing" onClick={(e) => scrollToSection(e, 'pricing')} className="text-sm font-medium text-slate-600 hover:text-purple-600 transition-colors">Pricing</a>
          </div>

          <div className="flex items-center gap-4">
            {!isLoaded ? (
              // Loading skeleton for navbar
              <div className="h-10 w-32 bg-slate-200 animate-pulse rounded-md"></div>
            ) : (
              <>
                <SignedOut>
                  <div className="hidden md:flex">
                    <SignInButton mode="modal" forceRedirectUrl="/dashboard">
                      <Button variant="ghost" className="text-slate-600 hover:text-purple-600 hover:bg-purple-50">
                        Sign In
                      </Button>
                    </SignInButton>
                  </div>
                  <SignInButton mode="modal" forceRedirectUrl="/dashboard">
                    <Button className="bg-slate-900 hover:bg-slate-800 text-white shadow-lg shadow-purple-500/20 transition-all hover:-translate-y-0.5">
                      Get Started <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </SignInButton>
                </SignedOut>
                <SignedIn>
                  <Button
                    onClick={() => navigate('/dashboard')}
                    className="bg-slate-900 hover:bg-slate-800 text-white shadow-lg shadow-purple-500/20 transition-all hover:-translate-y-0.5"
                  >
                    Go to Dashboard <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                  <UserButton afterSignOutUrl="/" />
                </SignedIn>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 pb-4 md:pt-32 md:pb-6 px-6 overflow-hidden 2xl:min-h-[937px] 2xl:flex 2xl:items-center">
        {/* Abstract Background Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-400/20 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl animate-float-delayed" />

        <div className="max-w-7xl mx-auto relative z-10 w-full">
          <div className="flex flex-col lg:flex-row items-center gap-16">

            {/* Text Content */}
            <div className="flex-1 text-center lg:text-left">
              <Badge variant="outline" className="mb-8 px-4 py-1.5 text-sm border-purple-200 bg-white/50 text-purple-700 backdrop-blur-sm animate-fade-in-up mx-auto lg:mx-0 shadow-lg shadow-purple-500/50">
                <Zap className="w-3.5 h-3.5 mr-2 fill-purple-500 text-purple-500" />
                AI-Powered Learning Assistant
              </Badge>


              <div className="space-y-8 mb-14">
                <h1 className="text-5xl md:text-7xl font-jack font-bold text-slate-900 tracking-tight leading-[1.1]">
                  Transform PDFs into <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                    Interactive Quizzes
                  </span>
                </h1>
                <p className="text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                  Upload any document and instantly generate quizzes, flashcards, and summaries.
                  Master any subject 10x faster with AI-driven personalized learning paths.
                </p>

                <div className="mt-4 flex items-center gap-2 justify-center lg:justify-start">
                  <span className="px-2 py-1 rounded-md bg-slate-100 text-xs font-semibold text-slate-600 border border-slate-200">15 Qs</span>
                  <span className="px-2 py-1 rounded-md bg-slate-100 text-xs font-semibold text-slate-600 border border-slate-200">Flashcards</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-20 lg:mb-0">
                {!isLoaded ? (
                  // Loading skeleton while Clerk initializes
                  <div className="h-14 px-10 bg-slate-200 animate-pulse rounded-lg w-48"></div>
                ) : (
                  <>
                    <SignedOut>
                      <SignInButton mode="modal" forceRedirectUrl="/dashboard">
                        <Button size="lg" className="h-14 px-10 text-xl bg-slate-900 hover:bg-slate-800 text-white shadow-xl shadow-purple-500/20 hover:scale-105 transition-all duration-300">
                          Try for Free <Brain className="ml-2 w-6 h-6" />
                        </Button>
                      </SignInButton>
                    </SignedOut>
                    <SignedIn>
                      <Button
                        size="lg"
                        onClick={() => navigate('/dashboard')}
                        className="h-14 px-10 text-xl bg-slate-900 hover:bg-slate-800 text-white shadow-xl shadow-purple-500/20 hover:scale-105 transition-all duration-300"
                      >
                        Go to Dashboard <ArrowRight className="ml-2 w-6 h-6" />
                      </Button>
                    </SignedIn>
                  </>
                )}
              </div>
            </div>


            {/* Hero Visual */}
            <div className="flex-1 h-full relative w-full max-w-xl lg:max-w-none">
              <div className="relative md:perspective-1000">
                <div className="relative bg-white/60 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-1 md:p-2 md:rotate-y-[-12deg] md:rotate-x-[5deg] transform-gpu transition-transform duration-500 hover:rotate-0 scale-100 min-h-[350px] md:min-h-[450px]">
                  <img
                    src="image.png"
                    alt="App Dashboard Preview"
                    className="rounded-lg w-full h-full object-cover shadow-lg"
                    loading="eager"
                  />


                  {/* Floating Action Cards */}
                  <div className="absolute -left-12 top-1/3 bg-white p-4 rounded-xl shadow-lg border border-slate-100 animate-float hidden md:block">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">Quiz Generated</p>
                        <p className="text-xs text-slate-500">Biology_Chapter_1.pdf</p>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="h-1.5 w-32 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full w-3/4 bg-green-500 rounded-full"></div>
                      </div>
                      <p className="text-[10px] text-slate-400 text-right">75% Accuracy</p>
                    </div>
                  </div>

                  <div className="absolute -right-8 bottom-1/4 bg-white p-4 rounded-xl shadow-lg border border-slate-100 animate-float-delayed hidden md:block">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Brain className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">Flashcards Ready</p>
                        <p className="text-xs text-slate-500">24 cards created</p>
                      </div>
                    </div>
                  </div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.4, ease: "easeOut" }}
                    className="absolute -bottom-6 -left-6 bg-white/90 backdrop-blur-md p-4 rounded-xl shadow-lg border border-slate-100 animate-float-slow hidden lg:block"
                  >
                    <div className="flex -space-x-2 mb-2">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="w-6 h-6 rounded-full bg-slate-200 border border-white flex items-center justify-center overflow-hidden">
                          <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${i + 5}`} alt="user" />
                        </div>
                      ))}
                    </div>
                    <p className="text-xs font-medium text-slate-700">Used by 850+ students today</p>
                  </motion.div>
                </div>
              </div>
            </div>

          </div>
        </div>

      </section>

      {/* Simple Tagline */}
      <div className={`py-1 text-center border-t border-slate-200/60 transition-opacity duration-300 ${showTagline ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        <p className="text-xl md:text-2xl font-semibold text-slate-700 mb-1">
          Join students learning smarter with AI
        </p>
        <ChevronDown className="w-6 h-6 mx-auto text-slate-400 animate-bounce" />
      </div>

      {/* Features Section - Bento Grid */}
      <section id="features" className="py-24 relative overflow-hidden bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4 tracking-tight">Everything you need to study smarter</h2>
            <p className="text-lg text-slate-600">Axora processes your documents to create a personalized learning experience.</p>
          </div>

          <BentoGrid className="lg:auto-rows-[300px]">
            {features.map((feature) => (
              <BentoCard key={feature.name} {...feature} />
            ))}
          </BentoGrid>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-slate-50/50 -skew-y-3 transform origin-top-left scale-110"></div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">How Axora Works</h2>
            <p className="text-lg text-slate-600">Three simple steps to master your study materials.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative" ref={containerRef}>

            {/* Animated Beams */}
            <div className="absolute inset-x-0 top-12 hidden md:block pointer-events-none">
              {/* We map the beams here, but we need the refs to be attached to the elements below first. 
                    Since AnimatedBeam needs refs to be populated, they will work on initial render/layout effect.
                */}
            </div>
            <AnimatedBeam
              containerRef={containerRef}
              fromRef={step1Ref}
              toRef={step2Ref}
              delay={1}
              duration={14}
              repeatDelay={2}
              pathColor="rgba(147, 51, 234, 0.2)"
              gradientStartColor="#9333ea"
              gradientStopColor="#3b82f6"
            />
            <AnimatedBeam
              containerRef={containerRef}
              fromRef={step2Ref}
              toRef={step3Ref}
              delay={4}
              duration={14}
              repeatDelay={2}
              pathColor="rgba(147, 51, 234, 0.2)"
              gradientStartColor="#9333ea"
              gradientStopColor="#3b82f6"
            />


            {[
              {
                step: '01',
                title: 'Upload',
                desc: 'Upload your course notes, textbooks, or research papers.',
                ref: step1Ref
              },
              {
                step: '02',
                title: 'Analyze',
                desc: 'Our AI analyzes the content to understand key concepts and relationships.',
                ref: step2Ref
              },
              {
                step: '03',
                title: 'Learn',
                desc: 'Start chatting, taking quizzes, or reviewing flashcards instantly.',
                ref: step3Ref
              }
            ].map((item, i) => (
              <div key={i} className="relative z-10 text-center group">
                <div className="w-24 h-24 mx-auto bg-white rounded-full border-4 border-slate-50 shadow-xl flex items-center justify-center mb-6 group-hover:-translate-y-2 transition-transform duration-300 z-20 relative" ref={item.ref}>
                  <span className="text-3xl font-bold text-slate-900 font-['Outfit']">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-balance px-4">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Simple, Transparent Pricing</h2>
          <p className="text-lg text-slate-600">Choose the plan that fits your study needs.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Starter */}
          <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Starter</div>
            <div className="text-4xl font-bold text-slate-900 mb-2">Free</div>
            <p className="text-slate-600 mb-6 font-medium">Forever free for basic use</p>
            <div className="space-y-4 mb-8">
              {['5 Documents per month', 'Basic Chat Analysis', '10 Quizzes/mo', 'Community Support'].map((feat, i) => (
                <div key={i} className="flex items-center gap-3 text-sm text-slate-600">
                  <Check size={16} className="text-slate-400" /> {feat}
                </div>
              ))}
            </div>
            <Button variant="outline" className="w-full text-slate-900 border-slate-200 hover:bg-slate-50">Get Started</Button>
          </div>

          {/* Pro */}
          <div className="bg-slate-900 p-8 rounded-2xl border border-slate-900 shadow-xl relative overflow-hidden transform md:-translate-y-4">
            <div className="absolute top-0 right-0 bg-gradient-to-bl from-purple-500 to-blue-500 text-white text-xs font-bold px-3 py-1 rounded-bl-xl">POPULAR</div>
            <div className="text-sm font-semibold text-purple-200 uppercase tracking-wide mb-2">Pro Scholar</div>
            <div className="text-4xl font-bold text-white mb-2">$12<span className="text-lg font-medium text-slate-400">/mo</span></div>
            <p className="text-slate-300 mb-6 font-medium">For serious students</p>
            <div className="space-y-4 mb-8">
              {['Unlimited Documents', 'Advanced AI Models (GPT-4)', 'Unlimited Quizzes & Flashcards', 'Exam Mode Simulator', 'Priority Support'].map((feat, i) => (
                <div key={i} className="flex items-center gap-3 text-sm text-slate-300">
                  <Check size={16} className="text-purple-400" /> {feat}
                </div>
              ))}
            </div>
            <Button className="w-full bg-white text-slate-900 hover:bg-slate-100 border-none font-semibold">Start Free Trial</Button>
          </div>

          {/* Team */}
          <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Team</div>
            <div className="text-4xl font-bold text-slate-900 mb-2">$49<span className="text-lg font-medium text-slate-400">/mo</span></div>
            <p className="text-slate-600 mb-6 font-medium">For study groups & clssses</p>
            <div className="space-y-4 mb-8">
              {['Everything in Pro', '5 Team Members', 'Collaborative Notes', 'Shared Flashcard Decks', 'Team Analytics'].map((feat, i) => (
                <div key={i} className="flex items-center gap-3 text-sm text-slate-600">
                  <Check size={16} className="text-slate-400" /> {feat}
                </div>
              ))}
            </div>
            <Button variant="outline" className="w-full text-slate-900 border-slate-200 hover:bg-slate-50">Contact Sales</Button>
          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-200 pt-36 pb-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-col items-center justify-center text-center mb-16">
            <h2 className="text-2xl font-bold text-slate mb-4">Axora</h2>
            <p className="text-slate-600 max-w-md text-sm mb-8 leading-relaxed">
              Transform PDFs into Interactive Quizzes. Master any subject 10x faster with AI-driven personalized learning paths.
            </p>

            <div className="flex items-center gap-6 mb-12">
              <a href="https://twitter.com" className="text-slate-400 hover:text-slate-900 transition-colors">
                <X size={20} />
              </a>
              <a href="https://github.com" className="text-slate-400 hover:text-slate-900 transition-colors">
                <Github size={20} />
              </a>
              <a href="mailto:contact@axora.com" className="text-slate-400 hover:text-slate-900 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
              </a>
            </div>

            <div className="flex items-center gap-8 text-[11px] font-bold text-slate-500 tracking-widest uppercase mb-16">
              <a href="https://gauravv.me/" target="_blank" rel="noreferrer" className="hover:text-slate-900 transition-colors">Portfolio</a>
              <a href="https://gauravv.me/" target="_blank" rel="noreferrer" className="hover:text-slate-900 transition-colors">Contact</a>
            </div>

            <div className="w-16 h-16 bg-white border border-slate-100 rounded-2xl flex items-center justify-center shadow-lg relative z-20">
              <img src="/logo.png" alt="Axora Logo" className="w-10 h-10 object-contain brightness-0 opacity-80" />
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-8 border-t border-slate-200 text-[10px] sm:text-xs font-semibold text-slate-500 tracking-wider uppercase">
            <p>© 2026 AXORA. ALL RIGHTS RESERVED.</p>
            <p>CRAFTED BY <a href="https://gauravv.me/" target="_blank" rel="noreferrer" className="text-slate-900 hover:text-blue-600 transition-colors">GAURAVV</a></p>
          </div>
        </div>

        {/* Giant Background Text */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[40%] text-[15vw] font-black text-slate-950/[0.07] tracking-tighter w-full text-center pointer-events-none select-none z-0">
          AXORA
        </div>
      </footer>
    </div>
  );
};

const App = () => {
  return (
    <ClerkProvider publishableKey={clerkPubKey} navigate={(to) => to}>
      <DocumentsProvider>
        <Router>
          <div className="font-sans text-slate-900 antialiased selection:bg-purple-100 selection:text-purple-900">
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route
                path="/dashboard"
                element={
                  <SignedIn>
                    <Dashboard />
                  </SignedIn>
                }
              />
              <Route
                path="/dashboard/*"
                element={
                  <SignedOut>
                    <RedirectToSignIn />
                  </SignedOut>
                }
              />
              <Route
                path="/chat/:documentId"
                element={
                  <>
                    <SignedIn>
                      <ChatInterface />
                    </SignedIn>
                    <SignedOut>
                      <RedirectToSignIn />
                    </SignedOut>
                  </>
                }
              />
              <Route
                path="/tools/:documentId"
                element={
                  <>
                    <SignedIn>
                      <StudyTools />
                    </SignedIn>
                    <SignedOut>
                      <RedirectToSignIn />
                    </SignedOut>
                  </>
                }
              />
            </Routes>
          </div>
        </Router>
      </DocumentsProvider>
    </ClerkProvider>
  );
};

export default App;